import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Search,
  BookOpen,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronUp,
  GripVertical
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const defaultScoringConfig = {
  mode: 'percent',
  tiers: [
    { tier_name: 'Platinum Clinician', min_value: 90, max_value: null, description: 'Exceptional priority, judgment, and patient safety.' },
    { tier_name: 'Gold Clinician', min_value: 75, max_value: 89, description: 'Highly competent clinical reasoning and safe practice.' },
    { tier_name: 'Silver Clinician', min_value: 60, max_value: 74, description: 'Developing clinician; some delays or inefficiencies in optimal care.' },
    { tier_name: 'Novice Tier', min_value: 0, max_value: 59, description: 'Significant clinical errors detected; remediation recommended.' }
  ]
};

const defaultStage = {
  title: '',
  vignette: '',
  question: {
    question_type: 'single_best',
    prompt: '',
    select_n_count: null,
    required_selections: 1,
    max_selections: 1,
    options: [
      { text: '', point_value: 0, category: 'standard_care', rationale: '' }
    ]
  }
};

export default function AdminCaseStudies() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Topics and subtopics for dropdowns
  const [availableTopics, setAvailableTopics] = useState([]);
  const [availableSubtopics, setAvailableSubtopics] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    overview: '',
    learning_objectives: [''],
    scoring_framework_text: '',
    topic_keys: [],
    selectedTopic: '',
    selectedSubtopic: '',
    stages: [{ ...defaultStage }],
    scoring_config: { ...defaultScoringConfig },
    status: 'draft'
  });
  
  const [expandedStages, setExpandedStages] = useState({ 0: true });

  useEffect(() => {
    fetchCases();
  }, [search, statusFilter, pagination.page]);

  // Fetch available topics on mount
  useEffect(() => {
    fetchTopics();
  }, []);

  // Fetch subtopics when topic changes
  useEffect(() => {
    if (formData.selectedTopic) {
      fetchSubtopics(formData.selectedTopic);
    } else {
      setAvailableSubtopics([]);
    }
  }, [formData.selectedTopic]);

  const fetchTopics = async () => {
    try {
      const res = await fetch(`${API}/api/admin/topics/all`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAvailableTopics(data);
      }
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  };

  const fetchSubtopics = async (topicKey) => {
    try {
      const res = await fetch(`${API}/api/admin/subtopics/${encodeURIComponent(topicKey)}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAvailableSubtopics(data);
      }
    } catch (err) {
      console.error('Failed to fetch subtopics:', err);
    }
  };

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: 20
      });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await fetch(`${API}/api/admin/case-studies?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch cases');
      const data = await res.json();
      setCases(data.case_studies);
      setPagination(data.pagination);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedCase(null);
    setFormData({
      title: '',
      overview: '',
      learning_objectives: [''],
      scoring_framework_text: 'You will be evaluated on: Critical Actions (life-saving interventions), Standard Care (appropriate diagnostic workup), Standard Errors (suboptimal but non-critical decisions), and Clinical Errors (potentially harmful decisions).',
      topic_keys: [],
      selectedTopic: '',
      selectedSubtopic: '',
      stages: [JSON.parse(JSON.stringify(defaultStage))],
      scoring_config: JSON.parse(JSON.stringify(defaultScoringConfig)),
      status: 'draft'
    });
    setAvailableSubtopics([]);
    setExpandedStages({ 0: true });
    setShowEditDialog(true);
  };

  const openEditDialog = async (caseStudy) => {
    try {
      const res = await fetch(`${API}/api/admin/case-studies/${caseStudy.case_id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch case details');
      const data = await res.json();
      
      // Extract topic and subtopic from topic_keys if available
      const topicKeys = data.topic_keys || [];
      const selectedTopic = topicKeys[0] || '';
      const selectedSubtopic = topicKeys[1] || '';
      
      setSelectedCase(data);
      setFormData({
        title: data.title || '',
        overview: data.overview || '',
        learning_objectives: data.learning_objectives?.length ? data.learning_objectives : [''],
        scoring_framework_text: data.scoring_framework_text || '',
        topic_keys: topicKeys,
        selectedTopic: selectedTopic,
        selectedSubtopic: selectedSubtopic,
        stages: data.stages?.length ? data.stages : [JSON.parse(JSON.stringify(defaultStage))],
        scoring_config: data.scoring_config || JSON.parse(JSON.stringify(defaultScoringConfig)),
        status: data.status || 'draft'
      });
      
      // Fetch subtopics if topic is set
      if (selectedTopic) {
        fetchSubtopics(selectedTopic);
      }
      
      const expanded = {};
      data.stages?.forEach((_, idx) => { expanded[idx] = false; });
      if (data.stages?.length > 0) expanded[0] = true;
      setExpandedStages(expanded);
      
      setShowEditDialog(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.overview.trim()) {
      toast.error('Overview is required');
      return;
    }
    if (!formData.selectedTopic) {
      toast.error('Topic is required');
      return;
    }
    if (formData.stages.length === 0) {
      toast.error('At least one stage is required');
      return;
    }
    
    // Validate stages
    for (let i = 0; i < formData.stages.length; i++) {
      const stage = formData.stages[i];
      if (!stage.title?.trim()) {
        toast.error(`Stage ${i + 1}: Title is required`);
        return;
      }
      if (!stage.vignette?.trim()) {
        toast.error(`Stage ${i + 1}: Vignette is required`);
        return;
      }
      if (!stage.question?.prompt?.trim()) {
        toast.error(`Stage ${i + 1}: Question prompt is required`);
        return;
      }
      if (!stage.question?.options?.length || stage.question.options.length < 2) {
        toast.error(`Stage ${i + 1}: At least 2 options are required`);
        return;
      }
      for (let j = 0; j < stage.question.options.length; j++) {
        if (!stage.question.options[j].text?.trim()) {
          toast.error(`Stage ${i + 1}, Option ${j + 1}: Text is required`);
          return;
        }
      }
    }
    
    setSaving(true);
    try {
      // Build topic_keys from selectedTopic and selectedSubtopic
      const topic_keys = [formData.selectedTopic];
      if (formData.selectedSubtopic) {
        topic_keys.push(formData.selectedSubtopic);
      }
      
      const payload = {
        ...formData,
        topic_keys,
        learning_objectives: formData.learning_objectives.filter(o => o.trim())
      };
      
      // Remove UI-only fields from payload
      delete payload.selectedTopic;
      delete payload.selectedSubtopic;
      
      const url = selectedCase
        ? `${API}/api/admin/case-studies/${selectedCase.case_id}`
        : `${API}/api/admin/case-studies`;
      
      const res = await fetch(url, {
        method: selectedCase ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success(selectedCase ? 'Case study updated' : 'Case study created');
      setShowEditDialog(false);
      fetchCases();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCase) return;
    
    try {
      const res = await fetch(`${API}/api/admin/case-studies/${selectedCase.case_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Failed to delete');
      
      toast.success('Case study deleted');
      setShowDeleteDialog(false);
      setSelectedCase(null);
      fetchCases();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Form helpers
  const addObjective = () => {
    setFormData(prev => ({
      ...prev,
      learning_objectives: [...prev.learning_objectives, '']
    }));
  };

  const updateObjective = (idx, value) => {
    setFormData(prev => ({
      ...prev,
      learning_objectives: prev.learning_objectives.map((o, i) => i === idx ? value : o)
    }));
  };

  const removeObjective = (idx) => {
    setFormData(prev => ({
      ...prev,
      learning_objectives: prev.learning_objectives.filter((_, i) => i !== idx)
    }));
  };

  const addStage = () => {
    const newIdx = formData.stages.length;
    setFormData(prev => ({
      ...prev,
      stages: [...prev.stages, JSON.parse(JSON.stringify(defaultStage))]
    }));
    setExpandedStages(prev => ({ ...prev, [newIdx]: true }));
  };

  const removeStage = (idx) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.filter((_, i) => i !== idx)
    }));
  };

  const updateStage = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((s, i) => i === idx ? { ...s, [field]: value } : s)
    }));
  };

  const updateQuestion = (stageIdx, field, value) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((s, i) => 
        i === stageIdx 
          ? { ...s, question: { ...s.question, [field]: value } }
          : s
      )
    }));
  };

  const addOption = (stageIdx) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((s, i) => 
        i === stageIdx
          ? {
              ...s,
              question: {
                ...s.question,
                options: [...s.question.options, { text: '', point_value: 0, category: 'standard_care', rationale: '' }]
              }
            }
          : s
      )
    }));
  };

  const updateOption = (stageIdx, optIdx, field, value) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((s, i) => 
        i === stageIdx
          ? {
              ...s,
              question: {
                ...s.question,
                options: s.question.options.map((o, j) => 
                  j === optIdx ? { ...o, [field]: value } : o
                )
              }
            }
          : s
      )
    }));
  };

  const removeOption = (stageIdx, optIdx) => {
    setFormData(prev => ({
      ...prev,
      stages: prev.stages.map((s, i) => 
        i === stageIdx
          ? {
              ...s,
              question: {
                ...s.question,
                options: s.question.options.filter((_, j) => j !== optIdx)
              }
            }
          : s
      )
    }));
  };

  const updateTier = (tierIdx, field, value) => {
    setFormData(prev => ({
      ...prev,
      scoring_config: {
        ...prev.scoring_config,
        tiers: prev.scoring_config.tiers.map((t, i) => 
          i === tierIdx ? { ...t, [field]: value } : t
        )
      }
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <BookOpen className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Case Studies</h1>
                <p className="text-slate-500">Create and manage clinical case studies</p>
              </div>
            </div>
            <Button
              onClick={openCreateDialog}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="create-case-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Case Study
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search cases..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-white border-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Case List */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading...</div>
        ) : cases.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Case Studies</h3>
              <p className="text-slate-500 mb-4">Create your first case study to get started.</p>
              <Button onClick={openCreateDialog} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Create Case Study
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {cases.map((caseStudy) => (
              <Card
                key={caseStudy.case_id}
                className="bg-white border-slate-200"
                data-testid={`case-row-${caseStudy.case_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900">{caseStudy.title}</h3>
                        <Badge className={
                          caseStudy.status === 'published'
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-amber-100 text-amber-700 border-amber-200'
                        }>
                          {caseStudy.status}
                        </Badge>
                      </div>
                      <p className="text-slate-500 text-sm line-clamp-2">{caseStudy.overview}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                        <span>{caseStudy.stages?.length || 0} stages</span>
                        {caseStudy.topic_keys?.length > 0 && (
                          <span>{caseStudy.topic_keys.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(caseStudy)}
                        className="text-slate-500 hover:text-slate-900"
                        data-testid={`edit-case-${caseStudy.case_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedCase(caseStudy);
                          setShowDeleteDialog(true);
                        }}
                        className="text-slate-500 hover:text-red-600"
                        data-testid={`delete-case-${caseStudy.case_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  className="border-slate-200"
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-slate-500">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  className="border-slate-200"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCase ? 'Edit Case Study' : 'Create Case Study'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Build a multi-stage clinical case with scored options.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid gap-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g., Acute Chest Pain Assessment"
                  data-testid="case-title-input"
                />
              </div>
              
              <div>
                <Label>Overview *</Label>
                <Textarea
                  value={formData.overview}
                  onChange={(e) => setFormData(p => ({ ...p, overview: e.target.value }))}
                  className="min-h-[80px]"
                  placeholder="Brief description of the case scenario..."
                  data-testid="case-overview-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Topic *</Label>
                  <Select
                    value={formData.selectedTopic}
                    onValueChange={(v) => setFormData(p => ({ 
                      ...p, 
                      selectedTopic: v,
                      selectedSubtopic: '' // Reset subtopic when topic changes
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTopics.map((topic) => (
                        <SelectItem key={topic.key} value={topic.key}>
                          {topic.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Subtopic dropdown - only show if topic is selected */}
              {formData.selectedTopic && (
                <div>
                  <Label>Subtopic (optional)</Label>
                  <Select
                    value={formData.selectedSubtopic || "none"}
                    onValueChange={(v) => setFormData(p => ({ ...p, selectedSubtopic: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a subtopic (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {availableSubtopics.map((subtopic) => (
                        <SelectItem key={subtopic.key} value={subtopic.key}>
                          {subtopic.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Learning Objectives */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Learning Objectives</Label>
                <Button size="sm" variant="ghost" onClick={addObjective} className="text-primary">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.learning_objectives.map((obj, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={obj}
                      onChange={(e) => updateObjective(idx, e.target.value)}
                      placeholder={`Objective ${idx + 1}`}
                    />
                    {formData.learning_objectives.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeObjective(idx)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Scoring Framework Text */}
            <div>
              <Label>Scoring Framework (shown to learners)</Label>
              <Textarea
                value={formData.scoring_framework_text}
                onChange={(e) => setFormData(p => ({ ...p, scoring_framework_text: e.target.value }))}
                className="min-h-[60px]"
                placeholder="Explain how the case will be scored..."
              />
            </div>

            {/* Stages */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-lg font-semibold">Stages</Label>
                <Button size="sm" onClick={addStage}>
                  <Plus className="w-3 h-3 mr-1" /> Add Stage
                </Button>
              </div>
              
              <div className="space-y-4">
                {formData.stages.map((stage, stageIdx) => (
                  <Card key={stageIdx} className="border">
                    <CardHeader
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpandedStages(p => ({ ...p, [stageIdx]: !p[stageIdx] }))}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-slate-400" />
                          <CardTitle className="text-base">
                            Stage {stageIdx + 1}: {stage.title || '(Untitled)'}
                          </CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          {formData.stages.length > 1 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); removeStage(stageIdx); }}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                          {expandedStages[stageIdx] ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {expandedStages[stageIdx] && (
                      <CardContent className="space-y-4 border-t">
                        <div>
                          <Label>Stage Title *</Label>
                          <Input
                            value={stage.title}
                            onChange={(e) => updateStage(stageIdx, 'title', e.target.value)}
                            placeholder="e.g., Initial Assessment"
                          />
                        </div>
                        
                        <div>
                          <Label>Clinical Vignette *</Label>
                          <Textarea
                            value={stage.vignette}
                            onChange={(e) => updateStage(stageIdx, 'vignette', e.target.value)}
                            className="min-h-[80px]"
                            placeholder="Describe the clinical scenario..."
                          />
                        </div>
                        
                        <div>
                          <Label>Question Prompt *</Label>
                          <Input
                            value={stage.question.prompt}
                            onChange={(e) => updateQuestion(stageIdx, 'prompt', e.target.value)}
                            placeholder="What should be your next step?"
                          />
                        </div>

                        {/* Selection Limits */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Required Selections *</Label>
                            <Input
                              type="number"
                              min="1"
                              value={stage.question.required_selections || 1}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                updateQuestion(stageIdx, 'required_selections', isNaN(val) || val < 1 ? 1 : val);
                              }}
                              placeholder="1"
                            />
                            <p className="text-xs text-slate-500 mt-1">Exact number learner must select</p>
                          </div>
                          <div>
                            <Label>Max Selections *</Label>
                            <Input
                              type="number"
                              min="1"
                              value={stage.question.max_selections || 1}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                updateQuestion(stageIdx, 'max_selections', isNaN(val) || val < 1 ? 1 : val);
                              }}
                              placeholder="1"
                            />
                            <p className="text-xs text-slate-500 mt-1">Max options learner can select</p>
                          </div>
                        </div>
                        
                        {/* Options */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>Answer Options</Label>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addOption(stageIdx)}
                              className="text-primary"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Add Option
                            </Button>
                          </div>
                          
                          <div className="space-y-3">
                            {stage.question.options.map((opt, optIdx) => (
                              <div key={optIdx} className="p-3 bg-slate-50 rounded-lg border">
                                <div className="flex items-start gap-2 mb-2">
                                  <Input
                                    value={opt.text}
                                    onChange={(e) => updateOption(stageIdx, optIdx, 'text', e.target.value)}
                                    className="flex-1"
                                    placeholder="Option text..."
                                  />
                                  {stage.question.options.length > 1 && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => removeOption(stageIdx, optIdx)}
                                      className="text-slate-400 hover:text-red-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                                <div className="grid grid-cols-3 gap-2 mb-2">
                                  <div>
                                    <Label className="text-xs text-slate-500">Points</Label>
                                    <Input
                                      type="number"
                                      value={opt.point_value}
                                      onChange={(e) => {
                                        const parsed = parseInt(e.target.value, 10);
                                        updateOption(stageIdx, optIdx, 'point_value', isNaN(parsed) ? 0 : parsed);
                                      }}
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <Label className="text-xs text-slate-500">Category</Label>
                                    <Select
                                      value={opt.category}
                                      onValueChange={(v) => updateOption(stageIdx, optIdx, 'category', v)}
                                    >
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="critical_action">Critical Action (+)</SelectItem>
                                        <SelectItem value="standard_care">Standard Care (+)</SelectItem>
                                        <SelectItem value="standard_error">Standard Error (-)</SelectItem>
                                        <SelectItem value="critical_error">Critical Error (-)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-500">Rationale (shown in debrief)</Label>
                                  <Textarea
                                    value={opt.rationale}
                                    onChange={(e) => updateOption(stageIdx, optIdx, 'rationale', e.target.value)}
                                    className="min-h-[50px] text-sm"
                                    placeholder="Explain why this choice is correct/incorrect..."
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Scoring Tiers */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-lg font-semibold">Performance Tiers</Label>
                <Select
                  value={formData.scoring_config.mode}
                  onValueChange={(v) => setFormData(p => ({
                    ...p,
                    scoring_config: { ...p.scoring_config, mode: v }
                  }))}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent-Based</SelectItem>
                    <SelectItem value="manual_range">Point-Based</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                {formData.scoring_config.tiers.map((tier, tierIdx) => (
                  <div key={tierIdx} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      value={tier.tier_name}
                      onChange={(e) => updateTier(tierIdx, 'tier_name', e.target.value)}
                      className="col-span-3"
                      placeholder="Tier name"
                    />
                    <Input
                      type="number"
                      value={tier.min_value}
                      onChange={(e) => updateTier(tierIdx, 'min_value', parseFloat(e.target.value) || 0)}
                      className="col-span-2"
                      placeholder="Min"
                    />
                    <span className="text-slate-500 text-center">to</span>
                    <Input
                      type="number"
                      value={tier.max_value ?? ''}
                      onChange={(e) => updateTier(tierIdx, 'max_value', e.target.value ? parseFloat(e.target.value) : null)}
                      className="col-span-2"
                      placeholder="Max (empty=no limit)"
                    />
                    <Input
                      value={tier.description}
                      onChange={(e) => updateTier(tierIdx, 'description', e.target.value)}
                      className="col-span-4"
                      placeholder="Description"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-case-btn"
            >
              {saving ? 'Saving...' : selectedCase ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Case Study?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{selectedCase?.title}&quot; and all associated user attempts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
